import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@features/auth/services/auth-service';
import { SessionUtils } from '@core/utils/session-utils';

import { logger } from '@core/utils/logger';
@Injectable({ providedIn: 'root' })
export class NavigationService {
  private router = inject(Router);
  private authService = inject(AuthService);

  /**
   * Navega según el estado de la sesión
   * Si tiene tableSessionId válido -> home (/session/:tableSessionId)
   * Si no tiene -> food-venues (NO scan-camera, porque no quiere escanear)
   */
  navigateBySessionState(delay = 100): void {
    setTimeout(() => {
      const tableSessionId = this.authService.tableSessionId();

      if (SessionUtils.isValidSession(tableSessionId)) {
        this.navigateToHome();
      } else {
        this.router.navigate(['/'], { replaceUrl: true });
      }
    }, delay);
  }

  /**
   * Navega al home si hay sesión válida
   * @returns true si navegó, false si no hay sesión válida
   */
  navigateToHomeIfHasSession(): boolean {
    const tableSessionId = this.authService.tableSessionId();

    if (SessionUtils.isValidSession(tableSessionId)) {
      this.navigateToHome();
      return true;
    }

    return false;
  }

  /**
   * Navega al home (/session/:tableSessionId)
   */
  navigateToHome(): void {
    const tableSessionId = this.authService.tableSessionId();

    if (!tableSessionId) {
      logger.warn('Navegación', 'Sin sesión de mesa: se redirige al inicio');
      this.router.navigate(['/']);
      return;
    }

    this.router.navigate(['/session', tableSessionId], { replaceUrl: true });
  }
  /**
   * Navega a una ruta dentro de session
   * Ejemplo: navigateToSessionRoute('menu/item/123')
   */
  navigateToSessionRoute(route: string): void {
    const tableSessionId = this.authService.tableSessionId();
    const cleanRoute = route.startsWith('/') ? route.substring(1) : route;
    this.router.navigate(['/session', tableSessionId, ...cleanRoute.split('/')]);
  }

  /**
   * Navega a food-venues
   */
  navigateToFoodVenues(): void {
    this.router.navigate(['/food-venues'], { replaceUrl: true });
  }

  /**
   * Navega al login
   */
  navigateToLogin(): void {
    const currentUrl = this.router.url;

    this.router.navigate(['/auth/login'], {
      queryParams: { returnUrl: currentUrl },
      replaceUrl: true,
    });
  }

  /**
   * Navega a la seleccion de roles disponibles
   */
  navigateToRoleSelection(): void {
    this.router.navigate(['/role-selection'], { replaceUrl: true });
  }

  navigateToRootDashboard(): Promise<boolean> {
    return this.router.navigate(['/root-admin'], { replaceUrl: true });
  }

  navigateToAdminDashboard(): Promise<boolean> {
    return this.router.navigate(['/admin']);
  }

  navigateToRootVenues(): void {
    this.router.navigate(['/root-admin/venues']);
  }

  navigateToScanHandler(tableId: string) {
    this.router.navigate(['/scan-qr', tableId]);
  }

  navigateToScanner() {
    this.router.navigate(['/scan-camera']);
  }

  navigateToPayments(opts?: { section?: 'history' | 'pending'; highlight?: string }): void {
    const tableSessionId = this.authService.tableSessionId();

    if (!SessionUtils.isValidSession(tableSessionId)) {
      this.router.navigate(['/']);
      return;
    }
    this.router.navigate(['/session', tableSessionId, 'payments'], {
      queryParams: {
        section: opts?.section ?? 'history',
        highlight: opts?.highlight,
      },
    });
  }

  navigateToBillSplit(): void {
    this.router.navigate(['/divisor-cuenta'], { replaceUrl: true });
  }
}
